import React, { useState, useCallback, useMemo } from 'react';
import TextBox from 'devextreme-react/text-box';
import { AntLocalization } from 'ante-react-kit';
import AntPickerPopup from '../AntPickerPopup';
import SearchView from './SearchView';
import CreateView from './CreateView';
import useCustomerSearch from './useCustomerSearch';
import useFilterSources from './useFilterSources';

export default function CustomerPicker({ dataField, value, formData, session, options, onValueChanged, disabled, autoOpen, onClosed, headless }) {
    const [open, setOpen] = useState(!!autoOpen);
    const [mode, setMode] = useState('SEARCH');
    const [createPrefillName, setCreatePrefillName] = useState('');
    const [activeFilters, setActiveFilters] = useState({});
    const [previewRow, setPreviewRow] = useState(null);

    const filterSources = useFilterSources({ enabled: open, session });

    const extraConditions = useMemo(() => {
        const cond = {};
        Object.keys(activeFilters).forEach((gKey) => {
            const set = activeFilters[gKey];
            if (set && set.size > 0) cond[gKey] = Array.from(set);
        });
        return cond;
    }, [activeFilters]);

    const { query, setQuery, rows, totalCount, loading, loadMore } = useCustomerSearch({
        enabled: open && mode === 'SEARCH',
        extraConditions
    });

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setMode('SEARCH');
        setOpen(true);
    }, [disabled]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setQuery('');
        setActiveFilters({});
        setMode('SEARCH');
        setPreviewRow(null);
        if (onClosed) onClosed();
    }, [setQuery, onClosed]);

    const handleRequestClose = useCallback(() => {
        if (mode === 'CREATE') {
            setMode('SEARCH');
            return;
        }
        handleClose();
    }, [mode, handleClose]);

    const handleToggleFilter = useCallback((groupKey, val) => {
        setActiveFilters((prev) => {
            const next = { ...prev };
            const cur = new Set(next[groupKey] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            next[groupKey] = cur;
            return next;
        });
    }, []);

    // Click card = preview only (mở right panel), KHÔNG chọn ngay
    const handlePreviewCard = useCallback((row) => {
        setPreviewRow(row);
    }, []);

    // Confirm "Chọn khách" từ right panel mới gọi onValueChanged
    const handleConfirmSelect = useCallback((row) => {
        if (!row) return;
        onValueChanged({
            entity_bus_code: row.code,
            entity_bus_id: row.id,
            entity_code: row.entity_code,
            entity_legal_id: row.entity_legal_id,
            entity_name: row.entity_name
        });
        handleClose();
    }, [onValueChanged, handleClose]);

    const handleGoCreate = useCallback((prefill) => {
        setCreatePrefillName(prefill || '');
        setMode('CREATE');
    }, []);

    const handleConfirmCreate = useCallback((patch) => {
        onValueChanged({
            entity_bus_code: null,
            entity_bus_id: null,
            entity_code: null,
            entity_name: patch.entity_name,
            entity_contact_phone: patch.entity_contact_phone,
            entity_legal_id: patch.entity_legal_id
        });
        handleClose();
    }, [onValueChanged, handleClose]);

    const displayName = value || (formData && formData.entity_name) || '';

    return (
        <>
            {!headless && (
                <div className={'apk-trigger' + (disabled ? ' apk-trigger--disabled' : '') + (displayName ? ' apk-trigger--filled' : '')}>
                    <TextBox
                        value={displayName}
                        readOnly={true}
                        disabled={disabled}
                        stylingMode="outlined"
                        showClearButton={false}
                        placeholder={AntLocalization.translateText({ vi: 'Chọn khách hàng...', en: 'Select customer...' })}
                        onFocusIn={handleOpen}
                    />
                </div>
            )}
            <AntPickerPopup
                visible={open}
                onClose={handleRequestClose}
                title={mode === 'SEARCH'
                    ? AntLocalization.translateText({ vi: 'Chọn khách hàng', en: 'Select Customer' })
                    : AntLocalization.translateText({ vi: 'Tạo khách hàng mới', en: 'Create Customer' })}
            >
                {mode === 'SEARCH' ? (
                    <SearchView
                        session={session}
                        query={query}
                        onQueryChange={setQuery}
                        rows={rows}
                        totalCount={totalCount}
                        loading={loading}
                        loadMore={loadMore}
                        activeFilters={activeFilters}
                        onToggleFilter={handleToggleFilter}
                        onPickCard={handlePreviewCard}
                        onConfirmSelect={handleConfirmSelect}
                        previewRow={previewRow}
                        onGoCreate={handleGoCreate}
                        filterSources={filterSources}
                    />
                ) : (
                    <CreateView
                        initialName={createPrefillName}
                        onCancel={() => setMode('SEARCH')}
                        onConfirm={handleConfirmCreate}
                    />
                )}
            </AntPickerPopup>
        </>
    );
}
